import { useState } from 'react';

import { Button, Modal, ModalActions, ModalContent, ModalWrapper, Radio, TextField, Typography } from '@braskai/ui-kit';

import { FormControlLabel, RadioGroup, Stack } from '@mui/material';

import { useCurrentUser } from '@/entities/user/currentUser';

import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { useInterfaceLanguage } from '@/shared/i18n';
import { MixpanelEvents, setMixpanelUserProperty } from '@/shared/thirdParty/mixpanel';
import { ModalHeaderDefault } from '@/shared/ui/modal';

import {
  ACQUISITION_SOURCE_LABEL_KEYS,
  ACQUISITION_SURVEY_DETAILS_MAX_LENGTH,
  type AcquisitionSource,
  DATA_TEST_IDS,
} from '../../lib/constants';
import { shuffleAcquisitionSources } from '../../lib/shuffleAcquisitionSources';
import { useAcquisitionSurveyStore } from '../../model/useAcquisitionSurveyStore';
import { useAcquisitionSurveyTrigger } from '../../model/useAcquisitionSurveyTrigger';

const AcquisitionSurveyModalComponent = () => {
  const { t } = useInterfaceLanguage();
  const close = useAcquisitionSurveyStore((state) => state.close);
  const { data: userId } = useCurrentUser((user) => user.id);
  const [sources] = useState(() => shuffleAcquisitionSources());
  const [source, setSource] = useState<AcquisitionSource | null>(null);
  const [details, setDetails] = useState('');

  useExecuteOneTime(() => MixpanelEvents.acquisitionSurveyShown());

  const skip = () => {
    MixpanelEvents.acquisitionSurveySkipped();
    close();
  };

  const submit = () => {
    if (!source) return;

    const sourceDetails = source === 'other' ? details.trim() : '';

    MixpanelEvents.acquisitionSurveySubmitted({ details: sourceDetails || undefined, source });

    if (userId) {
      setMixpanelUserProperty(userId, {
        acquisition_source: source,
        acquisition_source_details: sourceDetails || null,
      });
    }

    close();
  };

  return (
    <Modal data-testid={DATA_TEST_IDS.modal} size="sm" open onClose={skip}>
      <ModalHeaderDefault title={t('acquisitionSurvey.question')} onClose={skip} />

      <ModalContent>
        <Stack gap={8}>
          <RadioGroup
            aria-label={t('acquisitionSurvey.question')}
            value={source ?? ''}
            onChange={(event) => setSource(event.target.value as AcquisitionSource)}
          >
            {sources.map((option) => (
              <FormControlLabel
                key={option}
                label={
                  <Typography color="grey.900" variant="body-1">
                    {t(ACQUISITION_SOURCE_LABEL_KEYS[option])}
                  </Typography>
                }
                control={<Radio size="small" />}
                data-testid={DATA_TEST_IDS.option(option)}
                sx={{ gap: 8, margin: 0, padding: '6px 0' }}
                value={option}
              />
            ))}
          </RadioGroup>

          {source === 'other' ? (
            <TextField
              data-testid={DATA_TEST_IDS.details}
              placeholder={t('acquisitionSurvey.otherPlaceholder')}
              slotProps={{ htmlInput: { maxLength: ACQUISITION_SURVEY_DETAILS_MAX_LENGTH } }}
              value={details}
              variant="outlined"
              fullWidth
              onChange={(event) => setDetails(event.target.value)}
            />
          ) : null}
        </Stack>
      </ModalContent>

      <ModalActions>
        <Button data-testid={DATA_TEST_IDS.skip} size="lg" variant="outlined" fullWidth onClick={skip}>
          {t('acquisitionSurvey.skip')}
        </Button>
        <Button data-testid={DATA_TEST_IDS.submit} disabled={!source} size="lg" fullWidth onClick={submit}>
          {t('acquisitionSurvey.submit')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export const AcquisitionSurveyModal = () => {
  useAcquisitionSurveyTrigger();

  return (
    <ModalWrapper hook={useAcquisitionSurveyStore}>
      <AcquisitionSurveyModalComponent />
    </ModalWrapper>
  );
};
